// Turning an *ambient* identity-provider session into a PRISM session without
// navigating the page.
//
// The Help form on the login screen is the reason this exists. Attaching a
// screenshot requires a PRISM cookie (see help_feedback.py), but a clinician
// who is already signed in to TELUS has no PRISM cookie yet — only the IdP
// knows them. The ordinary fix, sending them through /api/auth/telus/login,
// is a full-page navigation that destroys whatever they have already typed
// into the form. So we run the same OAuth round trip inside a popup: the
// login page stays mounted, the form (and the File object the user picked,
// which could never survive a redirect) stays alive, and the popup comes to
// rest on a URL we read the outcome from.
//
// Reading the URL, rather than having the popup postMessage to us, is what
// makes this work behind the proxy. nginx adds its own Content-Security-Policy
// to everything under /api/ (see the frontend nginx config) and a browser
// enforces every policy it is served, so an inline script on the popup's
// landing page is blocked by `script-src 'self'` whatever the backend sends —
// which is exactly why the first version of this sat there with a blank
// window that never closed. Same-origin window.location is not script and
// cannot be blocked by any policy.
//
// Nothing here weakens the gate. The popup completes the identical authorize
// → callback → group-check → set-cookie flow; only the final URL differs. A
// user PingID does not recognise, or who is not in a PRISM group, comes back
// with ok:false and stays anonymous.

// Where a finished attempt parks. Must match _POPUP_DONE_PATH in
// backend/app/api/auth_telus.py.
const DONE_PATH = '/api/auth/telus/popup-done';

// Generous, because the window may now be showing an actual sign-in form:
// the backend escalates a declined prompt=none attempt into the interactive
// flow inside this same window, so this can span a password and MFA.
const TIMEOUT_MS = 5 * 60 * 1000;

// How often to look at the popup's URL. Well below what anyone perceives,
// and the read is a property access on a window we already hold.
const POLL_MS = 250;

function popupFeatures() {
  const w = 520;
  const h = 640;
  // Centre on the window the user is actually looking at, not on screen 0,
  // which on a multi-monitor desk is often not the one in front of them.
  const dualLeft = window.screenLeft ?? window.screenX ?? 0;
  const dualTop = window.screenTop ?? window.screenY ?? 0;
  const width = window.outerWidth || document.documentElement.clientWidth || w;
  const height = window.outerHeight || document.documentElement.clientHeight || h;
  const left = Math.max(0, Math.round(dualLeft + (width - w) / 2));
  const top = Math.max(0, Math.round(dualTop + (height - h) / 2));
  return `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`;
}

// The popup's URL, or null while it is away on the identity provider's origin
// (where any access throws a cross-origin SecurityError).
function readPopupUrl(popup) {
  try {
    const href = popup.location.href;
    if (!href || href === 'about:blank') return null;
    return new URL(href);
  } catch {
    return null;
  }
}

/**
 * Run the TELUS PingID flow in a popup.
 *
 * @param {{ silent?: boolean }} opts  silent adds prompt=none, so PingID
 *   answers from an existing session without rendering UI. If it declines,
 *   the backend escalates to the interactive flow in the same window rather
 *   than giving up — see the callback in auth_telus.py.
 * @returns {Promise<{ok: boolean, reason: string}>} reason is
 *   'popup_blocked' when the browser refused the window, 'closed' when the
 *   user dismissed it, 'timeout', or whatever status the backend parked on.
 *
 * MUST be called synchronously from a user gesture — awaiting anything first
 * detaches the call from the gesture and browsers will block the window.
 */
export function runTelusSsoPopup({ silent = false } = {}) {
  return new Promise((resolve) => {
    const url = `/api/auth/telus/login?mode=popup${silent ? '&silent=1' : ''}`;
    let popup = null;
    try {
      popup = window.open(url, 'prism_telus_sso', popupFeatures());
    } catch {
      popup = null;
    }
    if (!popup) {
      resolve({ ok: false, reason: 'popup_blocked' });
      return;
    }

    let settled = false;
    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearInterval(poll);
      clearTimeout(timer);
      try {
        if (!popup.closed) popup.close();
      } catch {
        /* cross-origin at the moment we gave up; nothing else to do */
      }
      resolve(result);
    };

    const poll = setInterval(() => {
      let closed = false;
      try {
        closed = popup.closed;
      } catch {
        return; // transiently unreadable; look again next tick
      }

      const current = readPopupUrl(popup);
      if (current && current.pathname === DONE_PATH) {
        // Only ever reached once the attempt is over — deliberately not the
        // callback path, which a silent attempt passes *through* on its way
        // to the interactive one and which would read as a false answer.
        const outcome = current.searchParams.get('status') || '';
        finish({ ok: outcome === 'ok', reason: outcome });
        return;
      }

      // Closed without reaching the landing page: dismissed, or a sign-in
      // the user decided against.
      if (closed) finish({ ok: false, reason: 'closed' });
    }, POLL_MS);

    const timer = setTimeout(() => finish({ ok: false, reason: 'timeout' }), TIMEOUT_MS);
  });
}
